import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { updateIdleSetting } from '@/api/tariffs'
import type { IdleSetting, IdleSettingStatus } from '@/types/tariffs'
import { InlineNumberInput } from './InlineNumberInput'

interface MutInput {
  status: IdleSettingStatus
  field: 'grace_minutes' | 'round_to_minutes'
  value: number
}

/**
 * §6.5 TARIFFS_SPEC.md — параметры тарификации простоя.
 * APPRAISAL и PAYMENT_TRANSFER × (грейс, округление). Inline + оптимистично.
 */
export function IdleSettingsTable({ rows }: { rows: IdleSetting[] }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const statusLabel: Record<IdleSettingStatus, string> = {
    APPRAISAL: t('tariffs.idle.statusAppraisal'),
    PAYMENT_TRANSFER: t('tariffs.idle.statusPaymentTransfer'),
  }
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const sorted = [...rows].sort((a, b) =>
    a.status === 'APPRAISAL' ? -1 : b.status === 'APPRAISAL' ? 1 : 0,
  )

  const mutation = useMutation({
    mutationFn: ({ status, field, value }: MutInput) =>
      updateIdleSetting(status, { [field]: value }),
    onMutate: async ({ status, field, value }) => {
      await qc.cancelQueries({ queryKey: ['tariffs', 'idle-settings'] })
      const prev = qc.getQueryData<IdleSetting[]>(['tariffs', 'idle-settings'])
      if (prev) {
        qc.setQueryData<IdleSetting[]>(['tariffs', 'idle-settings'], (cur) =>
          (cur ?? prev).map((r) =>
            r.status === status ? { ...r, [field]: value } : r,
          ),
        )
      }
      return { prev }
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['tariffs', 'idle-settings'], ctx.prev)
      const k = `${vars.status}.${vars.field}`
      setErrors((s) => ({
        ...s,
        [k]: err instanceof Error ? err.message : t('tariffs.errorGeneric'),
      }))
    },
    onSuccess: (_d, vars) => {
      const k = `${vars.status}.${vars.field}`
      setErrors((s) => {
        if (!s[k]) return s
        const next = { ...s }
        delete next[k]
        return next
      })
    },
    onSettled: () => {
      setBusyKey(null)
      void qc.invalidateQueries({ queryKey: ['tariffs', 'idle-settings'] })
    },
  })

  function commit(status: IdleSettingStatus, field: MutInput['field'], v: number | null) {
    if (v == null || !Number.isInteger(v) || v < 0) return
    setBusyKey(`${status}.${field}`)
    mutation.mutate({ status, field, value: v })
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[color:var(--stryzh-color-border-default)]">
      <table className="w-full text-left text-sm">
        <thead className="bg-[color:var(--stryzh-color-surface)] text-[color:var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2">{t('tariffs.colStatus')}</th>
            <th className="px-3 py-2">{t('tariffs.idle.colGrace')}</th>
            <th className="px-3 py-2">{t('tariffs.idle.colRound')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const graceKey = `${r.status}.grace_minutes`
            const roundKey = `${r.status}.round_to_minutes`
            return (
              <tr key={r.status} className="border-t border-[color:var(--stryzh-color-border-default)]">
                <td className="px-3 py-2 font-medium">{statusLabel[r.status]}</td>
                <td className="px-3 py-2">
                  <InlineNumberInput
                    value={r.grace_minutes}
                    onCommit={(v) => commit(r.status, 'grace_minutes', v)}
                    min={0}
                    width={96}
                    ariaLabel={t('tariffs.idle.ariaGrace', { status: r.status })}
                    invalid={Boolean(errors[graceKey])}
                    busy={busyKey === graceKey && mutation.isPending}
                  />
                  {errors[graceKey] ? (
                    <div className="stryzh-input-error-text mt-1">{errors[graceKey]}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <InlineNumberInput
                    value={r.round_to_minutes}
                    onCommit={(v) => commit(r.status, 'round_to_minutes', v)}
                    min={1}
                    width={96}
                    ariaLabel={t('tariffs.idle.ariaRound', { status: r.status })}
                    invalid={Boolean(errors[roundKey])}
                    busy={busyKey === roundKey && mutation.isPending}
                  />
                  {errors[roundKey] ? (
                    <div className="stryzh-input-error-text mt-1">{errors[roundKey]}</div>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
